/**
 * Accept Invite Form
 * Allows invited users to set their password and activate their account
 */

import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import authService from '@/services/authService'
import { validators } from '@/utils/validators'
import logger from '@/utils/logger'
import ValidationSummary from '@/components/common/ValidationSummary'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import '@/styles/pages/auth.css'
import type { ServerError, ValidationErrors } from '@/types'

interface FormData {
  password: string
  confirmPassword: string
}

const INITIAL_FORM_STATE: FormData = {
  password: '',
  confirmPassword: '',
}

function AcceptInviteForm(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const email = searchParams.get('email') || ''
  const expires = searchParams.get('expires') || ''
  const signature = searchParams.get('signature') || ''

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_STATE)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({})
  const [firstName, setFirstName] = useState('')
  const [isLinkValid, setIsLinkValid] = useState(false)

  useEffect(() => {
    const verifyInvite = async () => {
      if (!email || !signature) {
        setErrorMessage('Invalid invite link. Please check your email for the correct link.')
        setIsVerifying(false)
        return
      }

      try {
        const result = await authService.verifyInvite(email, expires, signature)
        if (result.valid) {
          setIsLinkValid(true)
          if (result.firstName) {
            setFirstName(result.firstName)
          }
        }
      } catch (error: unknown) {
        const verifyError = error as ServerError
        logger.error('Invite verification error:', verifyError)
        setErrorMessage(
          verifyError?.body?.message ||
          verifyError?.message ||
          'This invite link is invalid or has expired. Please contact support for assistance.'
        )
      } finally {
        setIsVerifying(false)
      }
    }

    verifyInvite()
  }, [email, expires, signature])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {}

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (!validators.minLength(formData.password, 8)) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    return newErrors
  }

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault()
    setErrorMessage('')
    setErrors({})
    setValidationErrors({})

    const newErrors = validateForm()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      await authService.acceptInvite(email, expires, signature, formData.password)
      // User is now logged in, redirect to dashboard
      navigate('/')
    } catch (error: unknown) {
      const acceptError = error as ServerError
      logger.error('Accept invite error:', acceptError)

      if (acceptError?.body) {
        const newValidationErrors: Record<string, string[]> = {}
        if (
          acceptError.body.errors &&
          typeof acceptError.body.errors === 'object'
        ) {
          Object.entries(acceptError.body.errors).forEach(
            ([field, messages]) => {
              newValidationErrors[field] = Array.isArray(messages)
                ? messages
                : [messages as string]
            }
          )
        }
        setValidationErrors(newValidationErrors)
        setErrorMessage(
          acceptError.body.message ||
            'Something went wrong. Please check the form and try again.'
        )
      } else {
        setErrorMessage(
          acceptError.message ||
            'Failed to set up your account. Please try again or contact support.'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isVerifying) {
    return <LoadingSpinner message="Verifying invite link..." />
  }

  if (isLoading) {
    return <LoadingSpinner message="Setting up your account..." />
  }

  if (!isLinkValid) {
    return (
      <div className="auth-form-container">
        <div className="auth-form-card">
          <h2>Invalid Invite Link</h2>
          <div className="alert alert-danger">{errorMessage}</div>
          <div className="auth-footer">
            <p>
              Already have an account? <Link to="/login">Login here</Link>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form-card">
        <h2>Set Up Your Account</h2>
        {firstName && (
          <p className="auth-welcome">
            Welcome{firstName ? `, ${firstName}` : ''}! Please create a password to complete your account setup.
          </p>
        )}
        {!firstName && (
          <p className="auth-welcome">
            Please create a password to complete your account setup.
          </p>
        )}

        <ValidationSummary
          errors={validationErrors}
          message={errorMessage}
        />
        {!validationErrors ||
        Object.keys(validationErrors).length === 0 ? (
          errorMessage && (
            <div className="alert alert-danger">{errorMessage}</div>
          )
        ) : null}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              className="form-control"
              disabled
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-control ${
                errors.password ? 'is-invalid' : ''
              }`}
              placeholder="Enter your password"
              disabled={isLoading}
              autoFocus
            />
            {errors.password && (
              <span className="form-text is-invalid">
                {errors.password}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`form-control ${
                errors.confirmPassword ? 'is-invalid' : ''
              }`}
              placeholder="Confirm your password"
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <span className="form-text is-invalid">
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={isLoading}
          >
            {isLoading ? 'Setting up...' : 'Set Up Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account? <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AcceptInviteForm
